// [2026-09-05] ADDED — the filter panel from the IncProctor Figma, shared by every table.
//
// EVERY CONTROL ON IT WORKS. The date range and Order By needed query parameters the API did not
// have; those were added in the same change (app/modules/me/route.py — date_from, date_to, order;
// app/core/me_analytics.py — _day_range, _dir, _sessions_sort). Shipping the panel with dead radios
// would have been the "flag that does nothing" the repo rules forbid.
//
// THE PANEL HOLDS A DRAFT AND ONLY COMMITS ON "Apply Filter". That is the design's shape and it is
// the right one for a filter with five inputs: applying each keystroke would fire five queries on
// the way to one intended result, and re-sort the table under the person mid-choice.
//
// "Clear" resets AND applies in one action. A Clear that only emptied the inputs would leave the
// table still filtered while the panel says it is not — the worst of both.

import { useEffect, useRef, useState } from 'react'
import { SearchIcon } from './icons.jsx'

// Quick Filter is a shorthand for the date range, not a separate concept: picking one fills From/To
// so the person can see exactly what it did, and can then adjust either end.
const QUICK = [
  { id: '1', label: 'Last 24 hours', days: 1 },
  { id: '3', label: 'Last 3 days', days: 3 },
  { id: '7', label: 'Last 7 days', days: 7 },
]

const ORDERS = [
  { id: 'desc', label: 'Newest to Oldest' },
  { id: 'asc', label: 'Oldest to Newest' },
  { id: 'name', label: 'Name ( A - Z)' },
]

const iso = (d) => d.toISOString().slice(0, 10)

export default function PfFilterPanel({
  label = 'Activity Logs',
  filters = [],          // [{ key, label, options }] — the caller's own selects, e.g. Status
  value,                 // { dateFrom, dateTo, order, ...filterValues }
  onApply,
  onClose,
  showOrderByName = false,
}) {
  const [draft, setDraft] = useState(value || {})
  const ref = useRef(null)

  // Re-seed when reopened, so the panel always shows what is actually applied rather than the
  // half-edited draft from the last time it was dismissed without applying.
  useEffect(() => { setDraft(value || {}) }, [value])

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))

  const pickQuick = (q) => {
    const to = new Date()
    const from = new Date(to.getTime() - (q.days - 1) * 86400000)
    set({ quick: q.id, dateFrom: iso(from), dateTo: iso(to) })
  }

  const orders = showOrderByName ? ORDERS : ORDERS.filter((o) => o.id !== 'name')

  return (
    <div className="pf-filter-panel" ref={ref} role="dialog" aria-label={`Filter ${label} by`}>
      <div className="pf-filter-head">
        <span>Filter {label} by</span>
        <SearchIcon />
      </div>

      <div className="pf-filter-body">
        <fieldset className="pf-filter-group">
          <legend>Quick Filter</legend>
          {QUICK.map((q) => (
            <label key={q.id} className="pf-radio">
              <input type="radio" name="pf-quick" checked={draft.quick === q.id}
                     onChange={() => pickQuick(q)} />
              <span>{q.label}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="pf-filter-group">
          <legend>Select {label} Date</legend>
          <div className="pf-filter-dates">
            <label>
              <span>From</span>
              {/* Changing a date by hand clears the Quick Filter selection — leaving it lit while
                  the range no longer matches it would state something untrue. */}
              <input type="date" value={draft.dateFrom || ''}
                     onChange={(e) => set({ dateFrom: e.target.value, quick: undefined })} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={draft.dateTo || ''}
                     onChange={(e) => set({ dateTo: e.target.value, quick: undefined })} />
            </label>
          </div>
        </fieldset>

        {filters.map((f) => (
          <fieldset className="pf-filter-group" key={f.key}>
            <legend>{f.label}</legend>
            <select className="pf-select" style={{ width: '100%' }}
                    aria-label={f.label}
                    value={draft[f.key] || ''}
                    onChange={(e) => set({ [f.key]: e.target.value || undefined })}>
              <option value="">Select…</option>
              {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </fieldset>
        ))}

        <fieldset className="pf-filter-group">
          <legend>Order By</legend>
          {orders.map((o) => (
            <label key={o.id} className="pf-radio">
              <input type="radio" name="pf-order" checked={(draft.order || 'desc') === o.id}
                     onChange={() => set({ order: o.id })} />
              <span>{o.label}</span>
            </label>
          ))}
        </fieldset>
      </div>

      <div className="pf-filter-foot">
        <button type="button" className="pf-btn"
                onClick={() => { onApply(draft); onClose() }}>Apply Filter</button>
        {/* Resets AND applies — see the header note. */}
        <button type="button" className="pf-link"
                onClick={() => { onApply({}); onClose() }}>Clear</button>
      </div>
    </div>
  )
}
